/**
 * Native push notifications (Capacitor) for the Marakin app shell.
 *
 * Web is a no-op: this whole module only does anything when running inside
 * the native iOS/Android wrapper (Capacitor.isNativePlatform()). On web the
 * functions resolve immediately so callers don't need to branch.
 *
 * Flow:
 *   1. After login, call initPush(). It registers listeners once, requests OS
 *      permission, and (on grant) registers with APNs/FCM.
 *   2. The 'registration' event yields a device token, which we POST to
 *      the backend (/push/tokens) so the server can target this device.
 *   3. Tapping a notification routes the user via window.location using the
 *      `url` field in the notification's data payload.
 *   4. On logout, call disablePush() to unregister the token so a shared
 *      device stops receiving the previous user's notifications.
 *   5. B-88: My account → Notifications can switch THIS device off for the
 *      signed-in person (turnOffPushOnDevice): the token is dropped on the
 *      server, the native registration is undone, and the choice is
 *      remembered per person in localStorage so initPush({ userId }) doesn't
 *      register it again on the next start or sign-in. turnOnPushOnDevice
 *      undoes it. What KINDS of push arrive is account-wide and server-side
 *      (GET/PUT /api/me/notification-preferences).
 *
 * The backend send helper (lib/push.js) sets data.url on every message.
 *
 * NOTE: we import the PushNotifications plugin statically rather than via a
 * dynamic `await import(...)`. Returning the Capacitor plugin proxy through an
 * async boundary makes the runtime probe it for `.then`, which the proxy
 * forwards to native as a method call → "PushNotifications.then() is not
 * implemented on android". The plugin is browser-safe to import, and we guard
 * every call behind isNative(), so web bundles are unaffected.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';

let listenersBound = false;
let lastSentToken = null;
let deviceUserId = null; // who initPush last ran for (B-88: the listener checks their switch)

const isNative = () => Capacitor.isNativePlatform();

// B-88: "off on this device", remembered per person (a shared family phone
// keeps each person's choice). Storage can throw (private mode): then the
// choice lasts until the app restarts, never longer than the server's copy.
const offKey = (userId) => `marakin_push_off:${userId}`;
export function isPushOffOnDevice(userId) {
  if (!userId) return false;
  try { return localStorage.getItem(offKey(userId)) === '1'; } catch { return false; }
}
function rememberOff(userId, off) {
  if (!userId) return;
  try {
    if (off) localStorage.setItem(offKey(userId), '1');
    else localStorage.removeItem(offKey(userId));
  } catch { /* see above */ }
}

async function sendTokenToBackend(token, platform) {
  if (!token || token === lastSentToken) return;
  try {
    await axios.post('/push/tokens', { token, platform });
    lastSentToken = token;
  } catch (err) {
    // Non-fatal: the user can still use the app; they just may not get pushes
    // on this device until the next registration.
    // eslint-disable-next-line no-console
    console.warn('[push] failed to register device token', err?.message || err);
  }
}

function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;

  PushNotifications.addListener('registration', (token) => {
    // B-88: a registration that lands after the person switched this device off.
    if (isPushOffOnDevice(deviceUserId)) return;
    sendTokenToBackend(token.value, Capacitor.getPlatform());
  });

  PushNotifications.addListener('registrationError', (err) => {
    // eslint-disable-next-line no-console
    console.error('[push] registration error', err);
  });

  // App in FOREGROUND: Android shows no OS banner when the app is focused,
  // so without this an open user sees nothing (UI tweak #7). Re-emit as a DOM
  // event; <PushBanner /> (mounted in index.jsx) renders an in-app banner that
  // taps through to the same deep link as an OS notification tap.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    window.dispatchEvent(new CustomEvent('marakin:push-foreground', {
      detail: {
        title: notification?.title || 'Marakin',
        body: notification?.body || '',
        url: notification?.data?.url || null,
      },
    }));
  });

  // User tapped a notification (foreground or background) — route to the
  // deep link the backend attached as data.url (an in-app path like
  // "/backwell-rfc?event=123").
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action?.notification?.data?.url;
    if (url && typeof url === 'string' && url.startsWith('/')) {
      window.location.assign(url);
    }
  });
}

/**
 * Request permission + register this device. Safe to call repeatedly
 * (listeners bind once). No-op on web. B-88: pass { userId } — nothing is
 * registered when that person switched this device off.
 */
export async function initPush({ userId } = {}) {
  if (!isNative()) return { ok: false, reason: 'not-native' };
  if (userId) deviceUserId = userId;
  if (isPushOffOnDevice(userId)) return { ok: false, reason: 'off-on-device' };
  try {
    bindListeners();

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      return { ok: false, reason: 'permission-denied' };
    }
    await PushNotifications.register();
    return { ok: true };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[push] initPush failed', err);
    return { ok: false, reason: 'error' };
  }
}

/** Current OS permission state ('granted' | 'denied' | 'prompt' | 'unsupported'). */
export async function getPushPermission() {
  if (!isNative()) return 'unsupported';
  try {
    const perm = await PushNotifications.checkPermissions();
    return perm.receive;
  } catch {
    return 'unsupported';
  }
}

/**
 * Unregister this device's token on the backend (call on logout). We tell the
 * backend to drop the token we most recently registered for this user.
 */
export async function disablePush() {
  const token = lastSentToken;
  lastSentToken = null;
  if (!isNative() || !token) return;
  try {
    await axios.delete('/push/tokens', { data: { token } });
  } catch {
    /* best effort */
  }
}

/**
 * B-88: switch push off on THIS device for this person. The server copy of
 * the token goes first: if that call fails nothing changes and { ok: false }
 * comes back (the switch stays on — never "off" while pushes still arrive).
 * Then the native registration is undone (Android deletes the FCM token, so
 * even a token the server still held would be pruned on the next send).
 */
export async function turnOffPushOnDevice(userId) {
  if (!isNative() || !userId) return { ok: false, reason: 'not-native' };
  const token = lastSentToken;
  if (token) {
    try {
      await axios.delete('/push/tokens', { data: { token } });
    } catch {
      return { ok: false, reason: 'network' };
    }
  }
  rememberOff(userId, true);
  lastSentToken = null;
  try { await PushNotifications.unregister(); } catch { /* best effort */ }
  return { ok: true };
}

/** B-88: switch push back on for this device (asks the OS if it never has). */
export async function turnOnPushOnDevice(userId) {
  if (!isNative() || !userId) return { ok: false, reason: 'not-native' };
  rememberOff(userId, false);
  lastSentToken = null; // register again even if FCM hands back the same token
  return initPush({ userId });
}

export const pushSupported = () => isNative();
